// Profile completion utility functions

export const calculateProfileCompletion = (profileData) => {
    if (!profileData) return { percentage: 0, missingFields: [], isComplete: false };

    console.log('[ProfileUtils] Calculating completion for:', profileData);

    // Check if backend already provided profile completion data
    const profileCompletion = profileData.profile_completion;
    
    if (profileCompletion) {
        // Use backend calculation directly for consistency
        console.log('[ProfileUtils] Using backend calculation:', profileCompletion);
        
        // Map backend data to frontend format
        const sectionStatus = {
            basic: profileCompletion.step1_completed || false,
            userType: profileCompletion.step2_completed || false,
            establishment: profileCompletion.step3_completed || false,
            banking: profileCompletion.step4_completed || false // Banking is part of step 3 in backend
        };
        
        // Determine missing sections for user feedback
        const missingFields = [];
        if (!sectionStatus.basic) missingFields.push({ field: 'basic_info', section: 'Basic Information' });
        if (!sectionStatus.userType) missingFields.push({ field: 'user_type', section: 'User Type' });
        if (!sectionStatus.establishment) missingFields.push({ field: 'establishment_info', section: 'Establishment Details' });
        
        const result = {
            percentage: profileCompletion.completion_percentage || 0,
            isComplete: profileCompletion.is_profile_complete || false,
            missingFields,
            sectionStatus,
            nextStep: getNextIncompleteStep(sectionStatus)
        };
        
        console.log('[ProfileUtils] Backend-based result:', result);
        return result;
    }

    // Fallback to frontend calculation if backend data not available
    console.log('[ProfileUtils] Falling back to frontend calculation');

    // Handle backend data structure - extract nested data
    const basicInfo = profileData.basic_info || {};
    const establishmentInfo = profileData.establishment_info || {};
    const bankDetails = profileData.bank_details || {};
    const userTypeInfo = profileData.user_type || {};
    const loanAgentDetails = profileData.loan_agent_details || {};

    // Create flat structure for compatibility
    const flatData = {
        // Basic info fields
        first_name: basicInfo.first_name || profileData.first_name,
        last_name: basicInfo.last_name || profileData.last_name,
        mobile_number: basicInfo.mobile_number || profileData.mobile_number,
        email: basicInfo.email || profileData.email,
        company_name: basicInfo.company_name || profileData.company_name,
        address: basicInfo.address || profileData.address,
        city: basicInfo.city || profileData.city,
        state: basicInfo.state || profileData.state,
        pincode: basicInfo.pincode || profileData.pincode,
        
        // User type selections
        user_type_selections: userTypeInfo.user_type_selections || profileData.user_type_selections,
        
        // Establishment fields
        type_of_establishment: establishmentInfo.type_of_establishment || profileData.type_of_establishment,
        establishment_name: establishmentInfo.establishment_name || profileData.establishment_name,
        contact_person_name: establishmentInfo.contact_person_name || profileData.contact_person_name,
        contact_person_mobile: establishmentInfo.contact_person_mobile || profileData.contact_person_mobile,
        contact_person_email: establishmentInfo.contact_person_email || profileData.contact_person_email,
        
        // Banking fields
        bank_account_name: bankDetails.bank_account_name || profileData.bank_account_name,
        account_number: bankDetails.account_number || profileData.account_number,
        account_type: bankDetails.account_type || profileData.account_type,
        ifsc_code: bankDetails.ifsc_code || profileData.ifsc_code,
        bank_name: bankDetails.bank_name || profileData.bank_name,
        
        // Conditional fields for loan agents
        total_loan_disbursements_per_month: loanAgentDetails.total_loan_disbursements_per_month || profileData.total_loan_disbursements_per_month,
        support_needed_cities: loanAgentDetails.support_needed_cities || profileData.support_needed_cities,
        referral_loan_products: loanAgentDetails.referral_loan_products || profileData.referral_loan_products,
        loan_products_handled: loanAgentDetails.loan_products_handled || profileData.loan_products_handled,
        active_cities: loanAgentDetails.active_cities || profileData.active_cities
    };

    console.log('[ProfileUtils] Flattened data:', flatData);

    // Use simplified 4-step approach to match backend (25% per step)
    let totalScore = 0;
    let missingFields = [];
    let sectionStatus = {};

    // Step 1: Basic information (25%)
    const basicFields = ['first_name', 'last_name', 'mobile_number', 'email', 'address', 'city', 'state', 'pincode'];
    const basicComplete = basicFields.every(field => {
        const hasValue = flatData[field] && flatData[field].toString().trim() !== '';
        if (!hasValue) missingFields.push({ field, section: 'Basic Information' });
        return hasValue;
    });
    if (basicComplete) totalScore += 25;
    sectionStatus.basic = basicComplete;

    // Step 2: User type (25%)
    let userTypeComplete = false;
    if (flatData.user_type_selections && flatData.user_type_selections.length > 0) {
        userTypeComplete = true;
    } else {
        missingFields.push({ field: 'user_type_selections', section: 'User Type' });
    }
    if (userTypeComplete) totalScore += 25;
    sectionStatus.userType = userTypeComplete;

    // Step 3: Establishment information (25%) - includes banking
    const establishmentFields = ['type_of_establishment', 'establishment_name'];
    const bankingFields = ['bank_account_name', 'account_number', 'ifsc_code', 'bank_name'];
    
    const establishmentComplete = establishmentFields.every(field => {
        const hasValue = flatData[field] && flatData[field].toString().trim() !== '';
        if (!hasValue) missingFields.push({ field, section: 'Establishment Information' });
        return hasValue;
    });
    
    const bankingComplete = bankingFields.every(field => {
        const hasValue = flatData[field] && flatData[field].toString().trim() !== '';
        if (!hasValue) missingFields.push({ field, section: 'Banking Details' });
        return hasValue;
    });
    
    const step3Complete = establishmentComplete && bankingComplete;
    if (step3Complete) totalScore += 25;
    sectionStatus.establishment = step3Complete;
    sectionStatus.banking = bankingComplete;

    // Step 4: Documents (25%) - simplified check
    // For now, assume documents step is complete if we have establishment info
    // In a real scenario, this should check document uploads
    const documentsComplete = establishmentComplete; // Simplified
    if (documentsComplete) totalScore += 25;

    const result = {
        percentage: totalScore,
        isComplete: totalScore === 100,
        missingFields,
        sectionStatus,
        nextStep: getNextIncompleteStep(sectionStatus)
    };

    console.log('[ProfileUtils] Fallback calculation result:', result);
    console.log('[ProfileUtils] Section status:', sectionStatus);
    console.log('[ProfileUtils] Missing fields:', missingFields);

    return result;
};

export const getNextIncompleteStep = (sectionStatus) => {
    if (!sectionStatus.basic) return 'basic';
    if (!sectionStatus.userType) return 'userType';
    if (!sectionStatus.establishment) return 'establishment';
    if (!sectionStatus.banking) return 'banking';
    return null;
};

export const getProfileCompletionMessage = (percentage) => {
    if (percentage === 100) return { text: 'Your profile is completed', severity: 'success' };
    if (percentage >= 75) return { text: 'Almost there! Complete your profile to access all features', severity: 'info' };
    if (percentage >= 50) return { text: 'Profile 50% complete. Please complete to unlock features', severity: 'warning' };
    return { text: 'Complete your profile to start using BureauBox', severity: 'error' };
};

export const shouldBlockFeature = (feature, profileCompletion) => {
    // Define which features require complete profile
    const restrictedFeatures = [
        // Credit Bureau Reports
        'equifax', 
        'experian',
        
        // Identity and Address Verification
        'pan-verification',
        'aadhaar-verification',
        'voter-id',
        'driving-licence',
        'passport-verification',
        'udyam-verification',
        'fssai-verification',
        'shop-establishment-verification',
        'trademark-verification',

        // Financial Verification
        'bank-statement',
        'mobile-bureau',

        // Employment Verification
        'employment-uan',
        'employment-esi',
        
        // Professional Verification
        'ca-membership',
        'advocate-verification',
        
        // Other features
        'credits',
        'shared-leads',
        
        // Block unknown services by default
        'unknown-service'
    ];

    // Always allow access to these features
    const alwaysAllowedFeatures = [
        'dashboard',
        'profile',
        'profile-complete',
        'settings',
        'support',
        'logout'
    ];

    if (alwaysAllowedFeatures.includes(feature)) return false;
    if (!profileCompletion.isComplete && restrictedFeatures.includes(feature)) return true;
    
    return false;
};